import { useState, useCallback, useRef } from "react";
import { toast } from "react-toastify";
import { OrderData, OrderFilters, PaginationData } from "../types/order";
import { getDateRange } from "../utils/dateUtils";
import api from "../../../../../features/auth/axiosConfig";

export const useOrders = () => {
  const [orders, setOrders] = useState<OrderData[]>([]);
  const [loading, setLoading] = useState(false);
  const [paginationData, setPaginationData] = useState<PaginationData>({
    totalRecords: 0,
    totalPages: 0,
    currentPage: 1,
    pageSize: 10,
  });

  const requestInProgress = useRef(false);

  const lastFilters = useRef<OrderFilters | null>(null);
  const lastPage = useRef<number | null>(null);
  const lastPageSize = useRef<number | null>(null);

  const fetchOrders = useCallback(async (pageNumber: number, pageSize: number, filters: OrderFilters) => {
    const sameFilters = JSON.stringify(filters) === JSON.stringify(lastFilters.current);
    const samePage = pageNumber === lastPage.current;
    const sameSize = pageSize === lastPageSize.current;

    if (sameFilters && samePage && sameSize) {
      console.log("⛔ Skipped! Same filter & pagination — no API call.");
      return;
    }

    // Save new filter state
    lastFilters.current = filters;
    lastPage.current = pageNumber;
    lastPageSize.current = pageSize;

    if (requestInProgress.current) return;
    requestInProgress.current = true;

    try {
      setLoading(true);

      const { from, to } = getDateRange(filters.timeRange || "Today", filters.customStartDate, filters.customEndDate);

      const url = `/app/v1/pos/order?pageNumber=${pageNumber}&pageSize=${pageSize}&dateFilter=custom&startDate=${from}&endDate=${to}&searchBy=${filters.searchTerm || ""}&staff_name=${
        filters.selectedStaff || ""
      }`;

      const response = await api.get(url);

      if (response.data && response.data.result) {
        setOrders(response.data.result);
        setPaginationData({
          totalRecords: response.data.totalRecords || 0,
          totalPages: response.data.totalPages || 0,
          currentPage: response.data.currentPage || 1,
          pageSize: response.data.pageSize || 10,
        });
      }
    } catch (error) {
      console.error("Error fetching orders:", error);
      toast.error("Failed to fetch orders");
    } finally {
      setLoading(false);
      requestInProgress.current = false;
    }
  }, []);

  return { orders, loading, paginationData, fetchOrders };
};
